import React, { useState, useEffect } from 'react';
import { 
  Share2, 
  MessageCircle, 
  Mail, 
  Link, 
  Gift, 
  Coins, 
  TrendingUp,
  Award,
  CheckCircle
} from 'lucide-react';
import { SocialShare, UserReferralStats } from '../../types/users';
import { useExampleData } from '../../data/example/ExampleDataProvider';
import { MockBadge } from '../common/MockBadge';
import { DataDisclaimer } from '../common/DataDisclaimer';

interface SocialShareWidgetProps {
  contentType: 'recipe' | 'review' | 'achievement' | 'leaderboard' | 'app_invite';
  contentId: string;
  contentTitle: string;
  contentDescription: string;
  contentImageUrl?: string;
  userReferralCode: string;
  onShare: (platform: string, shareData: any) => void;
  className?: string;
}

const SocialShareWidget: React.FC<SocialShareWidgetProps> = ({
  contentType,
  contentId,
  contentTitle,
  contentDescription,
  contentImageUrl,
  userReferralCode,
  onShare,
  className = ''
}) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [userStats, setUserStats] = useState<UserReferralStats | null>(null);

  const { getReferralStats, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('social-sharing');

  useEffect(() => {
    const baseUrl = 'https://app.zippyhealth.io';
    const trackingUrl = `${baseUrl}/${contentType}/${contentId}?ref=${userReferralCode}`;
    setShareUrl(trackingUrl);

    if (isExampleData) {
      // Load from centralized example data
      const exampleStats = getReferralStats();
      setUserStats(exampleStats as UserReferralStats);
    } else {
      // Load from real API
      // const apiStats = await fetchUserReferralStatsFromAPI();
      // setUserStats(apiStats);
      setUserStats(null);
    }
  }, [contentType, contentId, userReferralCode, isExampleData]);

  const platforms = [
    {
      id: 'facebook',
      name: 'Facebook',
      color: 'bg-blue-600',
      reward: 10,
      signupReward: 500,
      description: 'Share with friends and family'
    },
    {
      id: 'twitter',
      name: 'Twitter/X',
      color: 'bg-gray-900',
      reward: 10,
      signupReward: 500,
      description: 'Tweet to your followers'
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      color: 'bg-green-500',
      reward: 15,
      signupReward: 500,
      description: 'Share with contacts'
    },
    {
      id: 'copy_link',
      name: 'Copy Link',
      color: 'bg-gray-500',
      reward: 5,
      signupReward: 500,
      description: 'Copy to clipboard'
    }
  ];

  const handlePlatformShare = (platform: any) => {
    if (platform.id === 'copy_link') {
      navigator.clipboard.writeText(shareUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    }
    
    onShare(platform.id, {
      contentType,
      contentId,
      contentTitle,
      shareUrl,
      platform: platform.id,
      referralCode: userReferralCode
    });

    setTimeout(() => setShowShareModal(false), 1000);
  };

  return (
    <>
      <button
        onClick={() => setShowShareModal(true)}
        className={`flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors ${className}`}
      >
        <Share2 className="w-4 h-4" />
        <span>Share & Earn</span>
        {userStats && userStats.totalCoinsFromReferrals > 0 && (
          <span className="bg-yellow-500 text-yellow-900 text-xs px-2 py-1 rounded-full">
            {userStats.totalCoinsFromReferrals}
          </span>
        )}
      </button>

      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Example Data Disclaimer */}
            {isExampleData && (
              <DataDisclaimer 
                type="info"
                title="Example Social Sharing"
                message="This shows example social sharing statistics and rewards. Real sharing features will be available when you connect your social accounts."
                className="mb-4"
              />
            )}

            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-xl font-bold text-gray-900 flex items-center">
                  <Share2 className="w-6 h-6 mr-2 text-blue-600" />
                  Share & Earn Rewards
                  {isExampleData && <MockBadge variant="small" className="ml-2" />}
                </h3>
                <p className="text-gray-600 mt-1">Spread the word and earn ZippyCoins!</p>
              </div>
              <button onClick={() => setShowShareModal(false)} className="text-gray-400 hover:text-gray-600">
                ✕
              </button>
            </div>

            {/* Reward Information */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-3 mb-3">
                <Gift className="w-6 h-6 text-purple-600" />
                <div>
                  <h4 className="font-medium text-purple-900">Referral Rewards</h4>
                  <p className="text-sm text-purple-800">Earn coins for sharing and bringing new users!</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-bold text-blue-600">5-20</div>
                  <div className="text-xs text-gray-600">Coins per share</div>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-bold text-green-600">500</div>
                  <div className="text-xs text-gray-600">Coins per signup</div>
                </div>
                <div className="bg-white rounded-lg p-3">
                  <div className="text-lg font-bold text-purple-600">+250</div>
                  <div className="text-xs text-gray-600">Bonus when active</div>
                </div>
              </div>
            </div>

            {/* User Performance Stats */}
            {userStats && (
              <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3 mb-3">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                  <div>
                    <h4 className="font-medium text-green-900">Your Sharing Performance</h4>
                    <p className="text-sm text-green-800">Level {userStats.referralLevel} Referrer • {userStats.consecutiveDaysSharing} day streak</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-lg font-bold text-blue-600">{userStats.totalShares}</div>
                    <div className="text-xs text-gray-600">Total Shares</div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-lg font-bold text-green-600">{userStats.totalSignups}</div>
                    <div className="text-xs text-gray-600">Signups</div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-lg font-bold text-purple-600">{userStats.totalRetentions}</div>
                    <div className="text-xs text-gray-600">Active Users</div>
                  </div>
                  <div className="bg-white rounded-lg p-3">
                    <div className="text-lg font-bold text-yellow-600">{userStats.totalCoinsFromReferrals}</div>
                    <div className="text-xs text-gray-600">Coins Earned</div>
                  </div>
                </div>

                {/* Achievements */}
                {userStats.referralBadges.length > 0 && (
                  <div className="mt-3">
                    <div className="text-sm font-medium text-gray-700 mb-2">Recent Achievements</div>
                    <div className="flex flex-wrap gap-2">
                      {userStats.referralBadges.map((badge, index) => (
                        <span key={index} className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full flex items-center">
                          <Award className="w-3 h-3 mr-1" />
                          {badge}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Platform Performance */}
                {userStats.platformPerformance.facebook && (
                  <div className="mt-3 pt-3 border-t border-green-200">
                    <div className="text-sm font-medium text-gray-700 mb-2">Best Platform: Facebook</div>
                    <div className="text-xs text-gray-600">
                      {userStats.platformPerformance.facebook.shares} shares • 
                      {userStats.platformPerformance.facebook.signups} signups • 
                      {userStats.platformPerformance.facebook.conversionRate}% conversion rate
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Share Platforms */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-900">Choose Platform</h4>
              {platforms.map(platform => (
                <button
                  key={platform.id}
                  onClick={() => handlePlatformShare(platform)}
                  className="w-full flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 transition-all"
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-lg ${platform.color} flex items-center justify-center`}>
                      <Share2 className="w-5 h-5 text-white" />
                    </div>
                    
                    <div className="text-left">
                      <div className="font-medium text-gray-900">{platform.name}</div>
                      <div className="text-sm text-gray-600">{platform.description}</div>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="flex items-center space-x-2">
                      <Coins className="w-4 h-4 text-yellow-600" />
                      <span className="font-medium text-gray-900">{platform.reward}</span>
                    </div>
                    <div className="text-xs text-gray-500">+{platform.signupReward} per signup</div>
                  </div>
                </button>
              ))}
            </div>

            {copySuccess && (
              <div className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center">
                <CheckCircle className="w-4 h-4 mr-2" />
                Copied to clipboard!
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SocialShareWidget; 
